import { useModal } from '@/contexts/modal'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { PlayCircle, Info, AlertTriangle } from 'lucide-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Ticket } from '@/modules/Ticket/types/response'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getServiceSLA } from '@/modules/Service/services/sla'
import { ServiceSLA } from '@/modules/Service/types/sla'

dayjs.extend(utc)

interface TicketConvertInProgressProps {
    ticket?: Ticket
}

const ConvertInProgress: React.FC<TicketConvertInProgressProps> = ({
    ticket,
}) => {
    const { t } = useTranslation()
    const { setModal } = useModal()

    // Fetch SLA for this service
    const { data: slaData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES,
            ticket?.service_id,
            'sla',
            'convert-check',
        ],
        queryFn: () => getServiceSLA(ticket?.service_id as number),
        enabled: !!ticket?.service_id && ticket.service_id > 0,
    })

    const sla = slaData?.result as ServiceSLA | undefined

    // Calculate SLA response time violation
    const checkSLAViolation = () => {
        if (!sla || !ticket?.ticket_requested_at) return null

        // Get response time from effective_sla_rules
        const defaultRule =
            sla.effective_sla_rules?.find(
                (r) => r.ticket_type_group === 'default'
            ) || sla.effective_sla_rules?.[0]
        const responseTimeMinutes = defaultRule?.response_time_minutes

        if (!responseTimeMinutes) return null

        // Parse as UTC since backend stores in UTC
        const requestedAt = dayjs.utc(ticket.ticket_requested_at)
        const deadline = requestedAt.add(responseTimeMinutes, 'minute')
        const now = dayjs.utc()

        if (now.isAfter(deadline)) {
            const overdueMins = now.diff(deadline, 'minute')
            return {
                isViolated: true,
                overdueMinutes: overdueMins,
                deadline: deadline.format('DD/MM/YYYY HH:mm'),
            }
        }

        return null
    }

    const violation = checkSLAViolation()

    const handleStart = () => {
        setModal('TicketModals/StartTicketConfirm/index', {
            ticket,
        })
    }

    return (
        <div className="flex flex-col items-center justify-center py-6 gap-4">
            {/* SLA Violation Warning */}
            {violation?.isViolated && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <Typogaphy size="small" className="text-red-700">
                        {t(
                            'ticket-sla-response-violated',
                            'Vi phạm SLA phản hồi'
                        )}{' '}
                        - {t('ticket-sla-overdue-by', 'Quá hạn')}{' '}
                        <strong>
                            {violation.overdueMinutes >= 60
                                ? `${Math.floor(violation.overdueMinutes / 60)}h ${violation.overdueMinutes % 60}m`
                                : `${violation.overdueMinutes}m`}
                        </strong>
                    </Typogaphy>
                </div>
            )}

            {/* Info message */}
            <div className="flex items-center gap-2 text-gray-600 bg-blue-50 px-4 py-2 rounded-lg">
                <Info className="w-4 h-4 text-blue-500" />
                <Typogaphy size="small" className="text-blue-700">
                    {t('ticket-start-processing-hint')}
                </Typogaphy>
            </div>

            {/* Action button */}
            <Button
                onClick={handleStart}
                variant="contained"
                size="small"
                startIcon={<PlayCircle className="h-4 w-4" />}
            >
                {t('ticket-start-processing')}
            </Button>
        </div>
    )
}

export default ConvertInProgress
